<template>
  <div class="pain-card card" :class="'tone-' + tone" :style="toneStyle">
    <div class="pain-icon" :class="'lk' + tone" v-html="icon"></div>
    <h3>{{ title }}</h3>
    <p>{{ description }}</p>
  </div>
</template>

<script>
const TONES = {
  1: { scan: '#ff7883', title: '#ff8d97' },
  2: { scan: '#ff9a6c', title: '#ffa784' },
  3: { scan: '#ff5c8a', title: '#ff7a9b' },
  4: { scan: '#ffa07a', title: '#ffb593' }
};

export default {
  name: 'PainCard',
  props: {
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    icon: { type: String, default: '' },
    tone: { type: Number, default: 1 }
  },
  computed: {
    toneStyle() {
      const t = TONES[this.tone] || TONES[1];
      return { '--scan-c': t.scan, '--title-c': t.title };
    }
  }
};
</script>

<style lang="less" scoped>
.pain-card {
  padding: 30px;
  position: relative;
  overflow: hidden;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    width: 2px;
    height: 100%;
    background: linear-gradient(180deg, transparent 0%, var(--scan-c, #ff7883) 50%, transparent 100%);
    transform: translateY(-100%);
    transition: transform 0.9s cubic-bezier(0.45, 0.05, 0.55, 0.95);
    z-index: 2;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--scan-c, #ff7883), transparent);
    opacity: 0;
    transition: opacity 0.35s ease;
    z-index: 2;
    pointer-events: none;
  }

  &:hover::before {
    transform: translateY(100%);
  }
  &:hover::after {
    opacity: 0.9;
    transition: opacity 0.35s ease 0.3s;
  }
  &:hover .pain-icon {
    transform: translateY(-3px) scale(1.04);
  }
  &:hover h3 {
    color: #fff;
  }
}

.pain-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 18px;
  position: relative;
  transition: all 0.3s ease;

  &::after {
    content: '';
    position: absolute;
    inset: -4px;
    border-radius: 18px;
    background: inherit;
    filter: blur(10px);
    opacity: 0.18;
    z-index: -1;
    animation: painBreathe 3.2s ease-in-out infinite;
  }

  ::v-deep svg {
    width: 26px;
    height: 26px;
    stroke: #fff;
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  &.lk1 {
    background: linear-gradient(135deg, #ff7883 0%, #d94656 100%);
    box-shadow: 0 4px 12px rgba(255, 120, 131, 0.22);
  }
  &.lk2 {
    background: linear-gradient(135deg, #ff9a6c 0%, #e25e3a 100%);
    box-shadow: 0 4px 12px rgba(255, 154, 108, 0.22);
    &::after { animation-delay: 0.8s; }
  }
  &.lk3 {
    background: linear-gradient(135deg, #ff5c8a 0%, #c4336a 100%);
    box-shadow: 0 4px 12px rgba(255, 92, 138, 0.22);
    &::after { animation-delay: 1.6s; }
  }
  &.lk4 {
    background: linear-gradient(135deg, #ffa07a 0%, #d96846 100%);
    box-shadow: 0 4px 12px rgba(255, 160, 122, 0.22);
    &::after { animation-delay: 2.4s; }
  }
}

.pain-card h3 {
  font-size: 20px;
  font-weight: 700;
  color: var(--title-c, #fff);
  transition: color 0.25s ease;
}

.pain-card p {
  color: var(--muted);
  margin-top: 12px;
}

@keyframes painBreathe {
  0%, 100% { opacity: 0.12; }
  50% { opacity: 0.3; }
}

.pain-card.reveal.show:nth-child(2) { transition-delay: 0.14s; }
.pain-card.reveal.show:nth-child(3) { transition-delay: 0.28s; }
.pain-card.reveal.show:nth-child(4) { transition-delay: 0.42s; }
</style>
